import { createFileRoute, type ErrorComponentProps } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getOrderedCurrencies } from "@/lib/currencies";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { friendlyMessage } from "@/lib/errors";
import { useState, useRef, useEffect } from "react";
import Papa from "papaparse";
import { Copy, Download, Upload, Link2, MessageCircle, CheckCircle2, Unlink, Palette } from "lucide-react";
import { DangerZoneSeparate } from "@/components/DangerZoneSeparate";
import { DangerZoneDeleteAccount } from "@/components/DangerZoneDeleteAccount";
import { AccountAccessCard } from "@/components/AccountAccessCard";
import { ThemeSegmented } from "@/components/ThemeControls";
import { accountsQueryKey } from "@/lib/authScopedQueryKeys";
import { TRANSACTION_HEADER_SAFE_COLUMNS } from "@/lib/queries";

function SettingsErrorComponent({ reset }: ErrorComponentProps) {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Configuración</h1>
      <Card>
        <CardContent className="pt-6 space-y-3">
          <p className="text-sm text-muted-foreground">
            Ocurrió un error inesperado al cargar esta sección.
          </p>
          <Button variant="outline" onClick={reset}>Intentar de nuevo</Button>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/_app/settings")({
  component: SettingsPage,
  errorComponent: SettingsErrorComponent,
});

function SettingsPage() {
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const profileId = profile?.id;
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  // Mi perfil
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name ?? "");
      setPhone(profile.phone ?? "");
    }
  }, [profile?.id]);

  // Telegram linking
  const TELEGRAM_BOT = "LaJefa_AppBot";
  const [telegramToken, setTelegramToken] = useState<string | null>(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [unlinkingTelegram, setUnlinkingTelegram] = useState(false);

  // Query separada para el estado de Telegram (invalidable sin afectar el hook global useProfile)
  const telegramQ = useQuery({
    queryKey: ["telegram_status", profile?.id],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("telegram_user_id").eq("id", profile!.id).maybeSingle();
      return (data as any)?.telegram_user_id as string | null ?? null;
    },
    enabled: !!profile?.id,
    staleTime: 0,
  });
  const telegramUserId = telegramQ.data ?? null;

  async function generateTelegramToken() {
    setGeneratingToken(true);
    const { data, error } = await supabase.rpc("generate_telegram_link_token" as any);
    setGeneratingToken(false);
    if (error) { toast.error(friendlyMessage(error)); return; }
    setTelegramToken(data as string);
  }

  async function verifyTelegramLink() {
    if (!profile) return;
    const { data } = await supabase.from("profiles").select("telegram_user_id").eq("id", profile.id).maybeSingle();
    const linked = (data as any)?.telegram_user_id as string | null;
    if (linked) {
      await qc.invalidateQueries({ queryKey: ["telegram_status", profile.id] });
      setTelegramToken(null);
      toast.success("¡Telegram vinculado correctamente!");
    } else {
      toast.error("Todavía no se detectó la vinculación. ¿Ya tocaste Start en Telegram?");
    }
  }

  async function unlinkTelegram() {
    if (!profile) return;
    setUnlinkingTelegram(true);
    const { error } = await supabase.from("profiles").update({ telegram_user_id: null } as any).eq("id", profile.id);
    setUnlinkingTelegram(false);
    if (error) { toast.error(friendlyMessage(error)); return; }
    await qc.invalidateQueries({ queryKey: ["telegram_status", profile.id] });
    toast.success("Telegram desvinculado");
  }

  async function saveProfile() {
    if (!profile) return;
    const trimmedName = displayName.trim();
    if (trimmedName.length < 1 || trimmedName.length > 60) {
      toast.error("El nombre debe tener entre 1 y 60 caracteres.");
      return;
    }
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) {
      toast.error("El WhatsApp es requerido.");
      return;
    }
    if (!/^[+\d\s().-]{6,20}$/.test(trimmedPhone)) {
      toast.error("El WhatsApp no es válido.");
      return;
    }
    setSavingProfile(true);
    const { error } = await supabase.from("profiles").update({
      display_name: trimmedName,
      phone: trimmedPhone,
    }).eq("id", profile.id);
    setSavingProfile(false);
    if (error) { toast.error(friendlyMessage(error)); return; }
    await qc.invalidateQueries({ queryKey: ["profile"] });
    await qc.invalidateQueries({ queryKey: ["members_with_email", hh] });
    toast.success("Perfil actualizado");
  }

  const householdQ = useQuery({
    queryKey: ["household", hh],
    queryFn: async () => (await supabase.from("households").select("*").eq("id", hh!).maybeSingle()).data,
    enabled: !!hh,
  });
  const inviteCodeQ = useQuery({
    queryKey: ["invite_code", hh],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_my_invite_code");
      return (data as string) ?? "";
    },
    enabled: !!hh,
  });
  const membersQ = useQuery({
    queryKey: ["members_with_email", hh],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_household_members_with_email" as any);
      return (data ?? []) as { id: string; display_name: string; email: string }[];
    },
    enabled: !!hh,
  });

  const [name, setName] = useState<string>("");
  const [currency, setCurrency] = useState<string>("");
  const [accStartMonth, setAccStartMonth] = useState<string>("auto");
  const [savingHh, setSavingHh] = useState(false);

  // Sincronizar estado local con datos cargados (Select controlado).
  useEffect(() => {
    if (householdQ.data) {
      if (name === "") setName(householdQ.data.name ?? "");
      if (currency === "") setCurrency(householdQ.data.base_currency ?? "MXN");
      const m = (householdQ.data as any).accounting_start_month;
      setAccStartMonth(m == null ? "auto" : String(m));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdQ.data?.id]);
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  const isAlone = (membersQ.data?.length ?? 0) <= 1;

  async function joinHousehold() {
    setJoinError(null);
    const code = joinCode.trim().toUpperCase();
    if (!code) { setJoinError("Ingresa un código."); return; }
    setJoining(true);
    const { error } = await supabase.rpc("join_household_by_code", { _code: code });
    setJoining(false);
    if (error) {
      const msg = error.message.includes("invalid_code") ? "Código inválido."
        : error.message.includes("household_full") ? "Ese hogar ya tiene 2 miembros."
        : friendlyMessage(error);
      setJoinError(msg);
      return;
    }
    toast.success("Te uniste al hogar");
    setJoinCode("");
    qc.invalidateQueries();
  }

  function copyInviteLink() {
    const code = inviteCodeQ.data ?? "";
    if (!code) return;
    const link = `${window.location.origin}/auth?invite_code=${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Enlace copiado");
  }

  async function saveHousehold() {
    if (!hh) return;
    setSavingHh(true);
    const { error } = await supabase.from("households").update({
      name: name || householdQ.data?.name,
      base_currency: currency || householdQ.data?.base_currency,
      accounting_start_month: accStartMonth === "auto" ? null : Number(accStartMonth),
    }).eq("id", hh);
    setSavingHh(false);
    if (error) {
      toast.error(friendlyMessage(error));
      return;
    }
    // Invalidamos todo lo que depende de la moneda base / hogar.
    await qc.invalidateQueries({ queryKey: ["household"] });
    if (hh && profileId) {
      await qc.invalidateQueries({ queryKey: accountsQueryKey(hh, profileId), exact: true });
    }
    toast.success("Hogar actualizado");
  }

  async function exportCsv() {
    // Rebote #170 ronda 4, FIX 1: `select("*")` viola el GRANT column-level de transaction_headers
    // (refund_of_header_id ya no es legible por ningún select directo). El export nunca usó esa
    // columna (ver el mapeo de `rows` más abajo).
    // Rebote #170 ronda 5: sin mirar `error`, una lectura fallida exportaba un CSV vacío.
    const { data: headers, error: hErr } = await supabase.from("transaction_headers").select(TRANSACTION_HEADER_SAFE_COLUMNS).eq("household_id", hh!);
    if (hErr) {
      toast.error(friendlyMessage(hErr));
      return;
    }
    const { data: lines, error: lErr } = await supabase.from("transaction_lines").select("*").in("header_id", (headers ?? []).map((h) => h.id));
    if (lErr) {
      toast.error(friendlyMessage(lErr));
      return;
    }
    const headerMap = new Map((headers ?? []).map((h) => [h.id, h]));
    const rows = (lines ?? []).map((l) => {
      const h = headerMap.get(l.header_id);
      return {
        date: h?.date, description: h?.description, account_id: h?.account_id, type: h?.type,
        line_amount: l.amount, category_id: l.category_id, owner_id: l.owner_id, visibility: l.visibility,
        provision_id: l.provision_id, tag_id: l.tag_id,
      };
    });
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "movimientos.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  async function importCsv(file: File) {
    if (!hh || !profile) return;
    const [{ data: accs }, { data: cats }, { data: provs }, { data: tgs }] = await Promise.all([
      supabase.from("accounts").select("id").eq("household_id", hh),
      supabase.from("categories").select("id").eq("household_id", hh),
      supabase.from("provisions_and_goals").select("id").eq("household_id", hh),
      supabase.from("tags").select("id").eq("household_id", hh),
    ]);
    const accSet = new Set((accs ?? []).map((a) => a.id));
    const catSet = new Set((cats ?? []).map((c) => c.id));
    const provSet = new Set((provs ?? []).map((p) => p.id));
    const tagSet = new Set((tgs ?? []).map((t) => t.id));
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (res) => {
        const rows = res.data as any[];
        let ok = 0, fail = 0;
        const ALLOWED_TYPES = ["regular"] as const;
        for (const r of rows) {
          if (!r.date || !r.account_id || !r.line_amount) { fail++; continue; }
          if (!accSet.has(r.account_id)) { fail++; continue; }
          if (r.category_id && !catSet.has(r.category_id)) { fail++; continue; }
          if (r.provision_id && !provSet.has(r.provision_id)) { fail++; continue; }
          if (r.tag_id && !tagSet.has(r.tag_id)) { fail++; continue; }
          const total = parseFloat(r.line_amount);
          if (isNaN(total)) { fail++; continue; }
          const safeType = ALLOWED_TYPES.includes(r.type) ? r.type : null;
          if (r.type && !safeType) { fail++; continue; }
          // Rebote #170 ronda 4, FIX 1: `.select().single()` sin columnas pide "*" e implica leer
          // refund_of_header_id (permission denied). Sólo se usa `header.id` más abajo.
          const { data: header, error: hErr } = await supabase.from("transaction_headers").insert({
            household_id: hh, created_by: profile.id, account_id: r.account_id, date: r.date,
            total_amount: total, description: r.description ?? "", type: safeType || "regular",
          }).select("id").single();
          if (hErr || !header) { fail++; continue; }
          const { error: lErr } = await supabase.from("transaction_lines").insert({
            header_id: header.id, category_id: r.category_id || null, amount: total,
            owner_id: r.owner_id || profile.id, visibility: r.visibility || "shared",
            provision_id: r.provision_id || null, tag_id: r.tag_id || null,
          });
          if (lErr) fail++; else ok++;
        }
        toast.success(`Importadas ${ok} filas. ${fail ? `${fail} con error.` : ""}`);
        qc.invalidateQueries();
      },
    });
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Configuración</h1>
      <Card><CardHeader><CardTitle>Mi perfil</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Nombre</Label><Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={60} /></div>
          <div><Label>WhatsApp</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+54 9 11 1234-5678" /></div>
          <Button onClick={saveProfile} disabled={savingProfile}>{savingProfile ? "Guardando…" : "Guardar perfil"}</Button>
        </CardContent>
      </Card>
      <AccountAccessCard />

      {/* Apariencia / tema */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Apariencia
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Elegí el tema de la app. <strong>Sistema</strong> sigue la configuración de tu
            dispositivo (claro/oscuro) automáticamente.
          </p>
          <ThemeSegmented />
        </CardContent>
      </Card>

      {/* Telegram linking card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Conectar Telegram
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {telegramUserId ? (
            // Estado: vinculado
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                <span>Cuenta vinculada — podés registrar gastos desde @{TELEGRAM_BOT}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={unlinkTelegram}
                disabled={unlinkingTelegram}
                className="text-destructive border-destructive hover:bg-destructive/10"
              >
                <Unlink className="h-4 w-4 mr-2" />
                {unlinkingTelegram ? "Desvinculando…" : "Desvincular Telegram"}
              </Button>
            </div>
          ) : telegramToken ? (
            // Estado: token generado, esperando que el usuario abra Telegram
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Abrí Telegram y tocá <strong>Iniciar / Start</strong> cuando el bot te lo pida. El enlace expira en 15 minutos.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  asChild
                  className="bg-[#0088cc] hover:bg-[#006fa8] text-white"
                >
                  <a
                    href={`https://t.me/${TELEGRAM_BOT}?start=${telegramToken}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Abrir en Telegram
                  </a>
                </Button>
                <Button variant="outline" onClick={verifyTelegramLink}>
                  Ya lo hice — Verificar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                ¿No funcionó? <button className="underline" onClick={() => { setTelegramToken(null); }}>Generar nuevo enlace</button>
              </p>
            </div>
          ) : (
            // Estado: no vinculado
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Vinculá tu cuenta para registrar gastos directamente desde el bot de Telegram sin necesidad de abrir la app.
              </p>
              <Button onClick={generateTelegramToken} disabled={generatingToken}>
                <MessageCircle className="h-4 w-4 mr-2" />
                {generatingToken ? "Generando enlace…" : "Vincular Telegram"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card><CardHeader><CardTitle>Mi hogar</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>Moneda base</Label>
            {(() => {
              const { featured, rest } = getOrderedCurrencies(householdQ.data?.base_currency ?? currency ?? "MXN");
              return (
                <Select value={currency || "MXN"} onValueChange={setCurrency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {featured.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                    {featured.length > 0 && rest.length > 0 && <SelectSeparator />}
                    {rest.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              );
            })()}
            <p className="mt-1 text-xs text-muted-foreground">
              Moneda de referencia para calcular la proporción de aportes entre los miembros del hogar. Si la cambiás (por ejemplo al mudarte de país), los ingresos históricos registrados con otra moneda de referencia quedarán excluidos del cálculo hasta que completes la ventana en la nueva moneda.
            </p>
          </div>
          <div><Label>Código de invitación</Label>
            <div className="flex gap-2">
              <Input readOnly value={inviteCodeQ.data ?? ""} />
              <Button variant="outline" type="button" onClick={() => { navigator.clipboard.writeText(inviteCodeQ.data ?? ""); toast.success("Código copiado"); }}>
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="outline" type="button" onClick={copyInviteLink}>
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Comparte el código o el enlace completo con tu pareja (máx. 2 personas por hogar).</p>
          </div>
          <div>
            <Label>Mes de inicio del año contable</Label>
            <Select value={accStartMonth} onValueChange={setAccStartMonth}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automático (primer presupuesto)</SelectItem>
                <SelectSeparator />
                {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"].map((n, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Define desde qué mes se acumula el YTD (gasto y presupuesto anual). Útil si cargás histórico anterior a tu primer presupuesto.
            </p>
          </div>
          <Button onClick={saveHousehold} disabled={savingHh}>{savingHh ? "Guardando…" : "Guardar"}</Button>
        </CardContent>
      </Card>

      <Card><CardHeader><CardTitle>Miembros</CardTitle></CardHeader>
        <CardContent>
          {membersQ.data?.map((m) => (
            <div key={m.id} className="border-b py-2">
              <p className="font-medium">{m.display_name}</p>
              <p className="text-sm text-muted-foreground">{m.email}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {isAlone && (
        <Card>
          <CardHeader>
            <CardTitle>Unirme a un hogar existente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              ¿Creaste tu cuenta sola por error? Ingresa el código de tu pareja para unirte a su hogar. Tu hogar actual será eliminado si queda vacío.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="ABC12345"
                value={joinCode}
                onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setJoinError(null); }}
              />
              <Button onClick={joinHousehold} disabled={joining}>Unirme</Button>
            </div>
            {joinError && <p className="text-sm font-medium text-destructive">{joinError}</p>}
          </CardContent>
        </Card>
      )}

      <Card><CardHeader><CardTitle>Importar / Exportar datos</CardTitle></CardHeader>
        <CardContent className="flex gap-2">
          <Button variant="outline" onClick={exportCsv}><Download className="mr-2 h-4 w-4" /> Exportar CSV</Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()}><Upload className="mr-2 h-4 w-4" /> Importar CSV</Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importCsv(f); }} />
        </CardContent>
      </Card>

      {!isAlone && <DangerZoneSeparate />}
      <DangerZoneDeleteAccount />
    </div>
  );
}
